import {
  Component,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  BankAccount,
  BankAccountService,
  CreditCard,
  CreditCardService,
  EnumService,
  LocalStorageService,
} from '@ops/shared-types';
import { OpsInputComponent } from '../ops-input/ops-input.component';
import { OpsSelectComponent } from '../ops-select/ops-select.component';
import { OpsCalendarComponent } from '../ops-calendar/ops-calendar.component';

import { OpsInputPreviewComponent } from '../ops-input-preview/ops-input-preview.component';
import { Subscription } from 'rxjs';
import { OpsCheckboxComponent } from '../ops-checkbox/ops-checkbox.component';
import { format } from 'date-fns/format';
import { TranslateService } from '@ngx-translate/core';

/**
 * inputs:
 * -------
 * input label?: string
 * input placeholder?: string
 * input mandatory?: boolean
 * input formName?: string
 * input type text|email|phone|credit-card|cvc|password|custom
 * input customValidation? regexp|method
 * input noteText?: string
 * input isDisabled?: boolean
 * input setFocus?: boolean
 * input errorMessage?: string
 * input showError?: boolean
 * ---------------------------
 * events:
 * -------
 * output focused
 * output blured
 * output clicked
 * output changed
 * output validationError
 */

const dateNow: Date = new Date();

const CreditCardDefault = {
  bankAccount: '',
  company: '',
  createdAt: '',
  currency: '',
  currencyCode: '',
  cutOffDateDay: 1,
  deletedAt: '',
  description: '',
  expirationDate: new Date(
    Date.UTC(
      dateNow.getFullYear(),
      dateNow.getMonth() + 1,
      dateNow.getDate(),
      0,
      0,
      0,
      0
    )
  ).toISOString(),
  isActive: '',
  isFavorite: false,
  reservedFunds: '',
  settlementDateDay: 1,
  shortName: '',
  lastFourDigits: '',
};

@Component({
  selector: 'ops-credit-card-create',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    OpsInputComponent,
    OpsSelectComponent,
    OpsCheckboxComponent,
    OpsCalendarComponent,
    OpsInputPreviewComponent,
  ],
  templateUrl: './ops-credit-card-create.component.html',
  styleUrl: './ops-credit-card-create.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class OpsCreditCardCreateComponent implements OnInit, OnDestroy {
  creditCard = {
    bankAccount: '',
    company: '',
    createdAt: '',
    currency: '',
    currencyCode: '',
    cutOffDateDay: 1,
    deletedAt: '',
    description: '',
    expirationDate: new Date(
      Date.UTC(
        dateNow.getFullYear(),
        dateNow.getMonth() + 1,
        dateNow.getDate(),
        0,
        0,
        0,
        0
      )
    ).toISOString(),
    isActive: '',
    isFavorite: false,
    reservedFunds: '',
    settlementDateDay: 1,
    shortName: '',
    lastFourDigits: '',
  };

  cutOffDays = Array.from({ length: 28 }, (_, i) => i + 1 + '');

  mainCurrencies = [];
  creditCardBank;

  errorsTrack = {
    shortName: 'This field is required',
    description: 'This field is required',
    lastFourDigits: 'This field is required',
  };

  minExpirationDate: Date = new Date(
    Date.UTC(
      dateNow.getFullYear(),
      dateNow.getMonth() + 1,
      dateNow.getDate(),
      0,
      0,
      0,
      0
    )
  );
  maxExpirationDate: Date = new Date(
    Date.UTC(this.minExpirationDate.getFullYear() + 20, 11, 31, 0, 0, 0, 0)
  );

  subs: Array<Subscription> = [];

  @Output() closeModal = new EventEmitter<boolean | null>();

  constructor(
    private bankAccountService: BankAccountService,
    private creditCardsService: CreditCardService,
    private ls: LocalStorageService,
    private enumService: EnumService,
    private translateService: TranslateService
  ) {}

  bankAccounts = [];
  showBankAccountsModal = false;

  ngOnInit(): void {
    this.errorsTrack = {
      shortName: 'This field is required',
      description: 'This field is required',
      lastFourDigits: 'This field is required',
    };

    this.mainCurrencies = this.enumService
      .getCurrencyOptionsDetailed()
      .map((cop: any) => {
        return { key: cop.name, value: cop.shortName };
      });

    const bAccounts = this.bankAccountService
      .getAllBankAccountsForCompany(this.ls.getCurrentCompany()._id)
      .subscribe(
        (bankAccounts: BankAccount[]) => {
          this.bankAccounts = bankAccounts
            .filter((a) => !a.isDebit)
            .filter(
              (a) =>
                !['DEBIT', 'CASH', 'TEMP'].includes(a.bankName?.toUpperCase())
            )
            .filter(
              (a) =>
                !['DEBIT', 'CASH', 'TEMP'].includes(a.branchName?.toUpperCase())
            );

          this.bankAccounts = this.bankAccounts.map((value: BankAccount) => {
            return {
              key: value._id,
              value: value.shortName,
            };
          });

          if (this.bankAccounts.length) {
            this.creditCardBank = this.bankAccounts[0].value;
          }
        },
        (error) => {
          if (error?.error?.message) {
            const errorsList = error?.error?.message.split('.');
            const fullMessage = this.translateService.instant('windowAlert.opsCreditCardCreate.updateError', { errors: errorsList.join('.\n') });
            window.alert(fullMessage);
          }
        }
      );

    this.subs.push(bAccounts);
  }
  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  onSelectChangeForCC(value: string, field: string) {
    this.creditCardBank = value;
  }

  onSelectChangeForCCOpt(value: string, field: string) {
    this.creditCard[field] = value;
  }

  get isValid(): boolean {
    if (Object.keys(this.errorsTrack).length) {
      return false;
    }
    if (!this.creditCard.currencyCode) {
      return false;
    }

    if (!this.creditCard.expirationDate) {
      return false;
    }

    return true;
  }

  closeModalHandle = () => {
    this.closeModal.emit();
  };

  closeBankAccountsModal = () => {
    this.showBankAccountsModal = false;
  };

  datePreview = (val: Date) => {
    const dateCover = val.getDate();
    const monthCover = val.getMonth() + 1;

    return (
      (dateCover < 10 ? '0' + dateCover : dateCover) +
      '.' +
      (monthCover < 10 ? '0' + monthCover : monthCover) +
      '.' +
      val.getFullYear()
    );
  };

  onValueChange(selectValue: string, field?: string): void {
    this.creditCard[field] = selectValue;
  }

  cancelAddingCreditCard(event: Event) {
    this.creditCard = { ...CreditCardDefault };
  }

  saveNewCreditCard() {
    // Check if bank accounts are available
    if (!this.bankAccounts || this.bankAccounts.length === 0) {
      this.showBankAccountsModal = true;
      return;
    }

    const selectedBankKey = this.bankAccounts.find((ba) => {
      return ba.value === this.creditCardBank;
    }).key;

    // console.log(
    //   'this.creditCard.expirationDate',
    //   this.creditCard.expirationDate
    // );

    const [year, month, date] = this.creditCard.expirationDate
      .split('T')[0]
      .split('-');

    const creditCardClean = {
      ...this.creditCard,
      reservedFunds: 0,
      expirationDate: new Date(Date.UTC(+year, +month - 1, +date, 0, 0, 0, 0)),
      lastFourDigits: parseInt(this.creditCard.lastFourDigits),
      isActive: true,
      bankAccount: selectedBankKey,
    };

    // delete creditCardClean.endDate;
    delete creditCardClean.deletedAt;
    delete creditCardClean.createdAt;
    delete creditCardClean.currency;
    delete creditCardClean.company;

    this.creditCardsService
      .createCreditCard(creditCardClean, this.ls.getCurrentCompany()._id)
      .subscribe(async (newCreditCard: CreditCard) => {
        // console.log('NEW CREDIT CARD', newCreditCard);
        await this.enumService.refreshCache(this.ls.getCurrentCompany()._id);
        this.closeModal.emit(true);
      });
  }

  onErrorHandler(error: string, field?: string) {
    if (!error || error === '') {
      delete this.errorsTrack[field];
    } else {
      this.errorsTrack[field] = error;
    }
  }

  get creditCardBillingPeriod() {
    const cutOffStart = new Date();
    cutOffStart.setDate(this.creditCard.cutOffDateDay);

    const cutOffEnd = new Date();
    cutOffEnd.setMonth(cutOffEnd.getMonth() + 1);
    cutOffEnd.setDate(cutOffStart.getDate() - 1);

    const settlementDisplayedLTorEQcutOffDateDay =
      this.creditCard.settlementDateDay < this.creditCard.cutOffDateDay;

    if (settlementDisplayedLTorEQcutOffDateDay) {
      const settlementDisplayed = new Date();
      settlementDisplayed.setMonth(settlementDisplayed.getMonth() + 2);
      settlementDisplayed.setDate(this.creditCard.settlementDateDay);

      return `from ${format(cutOffStart, 'MMMdd').toUpperCase()} until ${format(
        cutOffEnd,
        'MMMdd'
      ).toUpperCase()} settled on ${format(
        settlementDisplayed,
        'MMMdd'
      ).toUpperCase()}`;
    }

    const settlementDisplayed = new Date();
    settlementDisplayed.setMonth(settlementDisplayed.getMonth() + 1);
    settlementDisplayed.setDate(this.creditCard.settlementDateDay);

    return `from ${format(cutOffStart, 'MMMdd').toUpperCase()} until ${format(
      cutOffEnd,
      'MMMdd'
    ).toUpperCase()} settled on ${format(
      settlementDisplayed,
      'MMMdd'
    ).toUpperCase()}`;
  }
}
